import { useMemo } from 'react';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import { UK_COUNTRY } from '@lib/utils/constants';
import { UNION_STATUS_TYPE } from '@lib/utils/constants/applicationConstants';
import { RATE_TYPE_VALUE_TO_LABEL } from '@lib/utils/constants/rateType';
import { formatRate } from '@lib/utils/rateHelper';

type RateItemProps = {
  unionStatus: string | null | undefined;
  unionRate: ApplicationRate | null | undefined;
  nonUnionRate: ApplicationRate | null | undefined;
  projectLocationCountry: string | null | undefined;
  ukRateNote: string | null | undefined;
  ukRateType: string | null | undefined;
  ukRateTypeOther: string | null | undefined;
};
export const RateItem = ({
  unionStatus,
  unionRate,
  nonUnionRate,
  projectLocationCountry,
  ukRateNote,
  ukRateType,
  ukRateTypeOther,
}: RateItemProps) => {
  const itemPieces = useMemo(() => {
    const result = [] as string[];
    if (
      unionStatus === UNION_STATUS_TYPE.SAG_AFTRA ||
      unionStatus === UNION_STATUS_TYPE.SAG_AFTRA_NON_UNION
    ) {
      const formattedRate = formatRate(unionRate);
      if (!formattedRate) {
        result.push('-');
      } else {
        result.push(`Union: ${formattedRate}`);
      }
    }
    if (
      unionStatus === UNION_STATUS_TYPE.NON_UNION ||
      unionStatus === UNION_STATUS_TYPE.SAG_AFTRA_NON_UNION
    ) {
      const formattedRate = formatRate(nonUnionRate);
      if (!formattedRate) {
        result.push('-');
      } else {
        result.push(`Non-Union: ${formattedRate}`);
      }
    }

    return result;
  }, [unionStatus, unionRate, nonUnionRate]);

  if (projectLocationCountry === UK_COUNTRY) {
    if (!ukRateType) {
      return <div>-</div>;
    }
    const ukRateTypeLabel =
      ukRateType === 'other'
        ? ukRateTypeOther || '-'
        : RATE_TYPE_VALUE_TO_LABEL[ukRateType] || '-';

    const isLongUkRateTypeLabel = ukRateType === 'other' && !!ukRateTypeOther;

    return (
      <StyledUkRateItem>
        <div
          className={isLongUkRateTypeLabel ? 'text-wrap-anywhere' : undefined}
        >
          {ukRateTypeLabel}
        </div>
        {!!ukRateNote?.trim() && (
          <div className='text-wrap-anywhere'>{ukRateNote}</div>
        )}
      </StyledUkRateItem>
    );
  }

  if (!unionStatus || !itemPieces?.length) {
    return <div>-</div>;
  }

  return (
    <StyledRateItem itemPiecesLength={itemPieces?.length}>
      {!!itemPieces?.[0] && (
        <Tooltip title={itemPieces?.[0]}>
          <div className='rate-item'>{itemPieces?.[0]}</div>
        </Tooltip>
      )}
      {itemPieces?.length > 1 && <div className='divider' />}
      {!!itemPieces?.[1] && (
        <Tooltip title={itemPieces?.[1]}>
          <div className='rate-item'>{itemPieces?.[1]}</div>
        </Tooltip>
      )}
    </StyledRateItem>
  );
};

const StyledUkRateItem = styled.div`
  display: flex;
  flex-direction: column;
`;

type StyledRateItemProps = {
  itemPiecesLength: number;
};
const StyledRateItem = styled.div<StyledRateItemProps>`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  .divider {
    width: 4px;
    height: 4px;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.palette.text[9]};
  }

  .rate-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: ${({ itemPiecesLength }) =>
      itemPiecesLength === 1 ? '100%' : 'calc(0.5 * (100% - 20px))'};
  }
`;
